import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { DateTime } from "luxon"
import { getTeamMemberVacationRequests } from "../../../../services/vacation.service"
import useScreenDimensionsContext from "../../../../hooks/contextConsumers/useScreenDimensionsContext.hook"
import useFetchTeamMemberVacationEvents from "../../../../hooks/useFetchTeamMemberVacationEvents.hook"
import useFloatingShowMore from "../../../../hooks/useFloatingShowMore.hook"
import BigCalendar from "../../../../components/compounds/bigCalendar/BigCalendar.compound"
import VacationRequestCard, {
  VacationRequestCardSkeleton,
} from "../../components/VacationRequestCard.compound"
import YearView from "../../../../components/compounds/bigCalendar/YearView.compound"
import Badge from "../../../../components/atoms/Badge.atom"
import Card from "../../../../components/atoms/Card.atom"
import GroupedEventsOverlayPanel from "../../components/GroupedEventsOverlayPanel.compound"
import FloatingButton from "../../components/FloatingButton.molecule"
import TeamMemberVacationRequestsContextMenu from "../../components/TeamMemberVacationRequestsContextMenu"
import TeamMemberVacationsMobileHeader from "./TeamMemberVacationsMobileHeader.controller"
import {
  EVENT_TYPES,
  MY_VACATION_STATES,
  TEAM_VACATION_REQUESTS_FILTER_OPTIONS,
  VACATION_STATES,
} from "../../../../consts/modules/vacations.consts"
import { BIG_CALENDAR_VIEWS } from "../../../../consts/bigCalendar.consts"
import {
  generatePeriodEndEvent,
  generatePeriodStartEvent,
  isEventHolidayOfAnyType,
  isEventPeriodDelimiter,
  getVacationRequestPriority,
  getVacationState,
  isVacationRequestApproved,
  isVacationRequestPending,
} from "../../../../utils/vacation.utils"
import { capitalizeFirstLetter } from "../../../../utils/strings.utils"
import { formatVacationEventsTemplates } from "../../../../utils/bigCalendar.utils"

const MobileTeamMemberData = ({
  selectedPeriod = [],
  setVisibleModal,
  selectedVacationRequest = null,
  setSelectedVacationRequest = () => {},
  refresh,
  triggerRefresh,
}) => {
  const [componentToShow, setComponentToShow] = useState("Summary")

  const [calendarVacationRequests, setCalendarVacationRequests] = useState([])
  const [groupedEvents, setGroupedEvents] = useState([])
  const [currentView, setCurrentView] = useState(BIG_CALENDAR_VIEWS.MONTH)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [clickedDay, setClickedDay] = useState(null)
  const [calendarLoading, setCalendarLoading] = useState(false)
  const { screenWidth } = useScreenDimensionsContext()
  const { fetchVacationRequests, fetchHolidays } =
    useFetchTeamMemberVacationEvents({
      selectedPeriod,
    })
  const opRef = useRef(null)

  const [summaryVacationRequests, setSummaryVacationRequests] = useState([])
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState(
    TEAM_VACATION_REQUESTS_FILTER_OPTIONS
  )
  const requestElementRef = useRef(null)
  const listContainerRef = useRef(null)
  const [submittingCancel, setSubmittingCancel] = useState(false)
  const showSeeMore = useFloatingShowMore(listContainerRef, [
    summaryVacationRequests,
    selectedFilter,
  ])
  const cmRef = useRef()

  useEffect(() => {
    fetchCalendarData()
    fetchSummaryData()
    setCurrentDate(selectedPeriod[0].toJSDate())
  }, [selectedPeriod, refresh])

  const fetchCalendarData = async () => {
    setCalendarLoading(true)
    setCalendarVacationRequests([])
    try {
      const vacationRequestsResponse = await fetchVacationRequests()
      const holidaysResponse = await fetchHolidays()
      console.log("Holidays", holidaysResponse)
      setCalendarVacationRequests([
        generatePeriodEndEvent(selectedPeriod[0].minus({ days: 1 })),
        generatePeriodStartEvent(selectedPeriod[0]),
        ...vacationRequestsResponse,
        ...holidaysResponse,
        generatePeriodEndEvent(selectedPeriod[1]),
        generatePeriodStartEvent(selectedPeriod[1].plus({ days: 1 })),
      ])
    } catch (error) {
      console.error("Error fetching vacation requests:", error)
      setCalendarVacationRequests([])
    } finally {
      setCalendarLoading(false)
    }
  }

  const fetchSummaryData = async () => {
    setSummaryLoading(true)
    setSummaryVacationRequests([])
    try {
      const response = await getTeamMemberVacationRequests(
        selectedPeriod[0].toISODate(),
        selectedPeriod[1].toISODate()
      )
      if (!response.success) {
        throw new Error(response.message)
      }
      setSummaryVacationRequests(response.result)
    } catch (error) {
      console.error("Error fetching vacation requests:", error)
      setSummaryVacationRequests([])
    } finally {
      setSummaryLoading(false)
    }
  }

  const handleNavigate = (date, e) => {
    if (DateTime.fromJSDate(date) < selectedPeriod[0]) {
      return
    }
    opRef?.current?.hide(e)
    setCurrentDate(date)
  }
  const eventPropGetter = (event) => {
    const eventStart = DateTime.fromJSDate(event.start)
    const visibleMonth = DateTime.fromJSDate(currentDate).month
    const isEventOutsideVisibleMonth =
      currentView === BIG_CALENDAR_VIEWS.MONTH &&
      eventStart.month !== visibleMonth
    if (isEventOutsideVisibleMonth) {
      return {
        style: {
          opacity: 0.5,
        },
      }
    }
    if ("groupedEvents" in event) {
      return {
        className: "grouped-event",
      }
    }
    return {}
  }
  const handleEventClick = (
    event,
    e,
    options = {
      groupedEvents: true,
    }
  ) => {
    if (calendarLoading) return
    if (isEventHolidayOfAnyType(event)) return
    if (isEventPeriodDelimiter(event)) {
      return
    }
    const selectedEvents =
      currentView === BIG_CALENDAR_VIEWS.MONTH && options.groupedEvents ?
        groupedEvents
      : calendarVacationRequests

    if (!selectedEvents[event.id]?.groupedEvents) {
      setSelectedVacationRequest({
        ...event,
        id: event?.idVacations || event.id,
      })
      setVisibleModal(MY_VACATION_STATES.QUERY)
      opRef?.current?.hide(e)
      return
    }
    const element = document.querySelector(".hovered-cell")
    if (element) {
      setClickedDay(
        DateTime.fromISO(element.classList[1]).startOf("day").toJSDate()
      )
      setTimeout(() => {
        const newTarget = document.querySelector(
          "." +
            Array.from(e.target.classList).join(".") +
            '[title="' +
            e.target.title +
            '"]'
        )
        if (newTarget && newTarget !== e.target) {
          e.target = newTarget
        }
      }, 0)
    }
    opRef?.current?.hide(e)
    setTimeout(() => {
      opRef?.current?.show(e)
    }, 0)
  }

  const getFilteredRequests = (vacationRequests) => {
    return vacationRequests
      .filter((item) => {
        if (
          !selectedFilter.rejected_requests &&
          getVacationState(item) === VACATION_STATES.REJECTED
        ) {
          return false
        }
        if (
          !selectedFilter.past_requests &&
          getVacationState(item) === VACATION_STATES.BLOCKED
        ) {
          return false
        }
        return true
      })
      .map((item) => ({
        ...item,
        priority: getVacationRequestPriority(item),
      }))
  }
  const groupRequestsByMonth = (vacationRequests) => {
    return vacationRequests.reduce((acc, request) => {
      const date = new Date(request.startDate)
      const monthName = `${date.toLocaleString("es-ES", { month: "long" })} ${date.getFullYear()}`
      if (!acc[monthName]) {
        acc[monthName] = []
      }
      acc[monthName].push(request)
      return acc
    }, {})
  }
  const renderVacationRequests = () => {
    const filteredRequests = getFilteredRequests(summaryVacationRequests)

    const sortedRequests = filteredRequests.sort(
      (a, b) => a.priority - b.priority || a.startDate - b.startDate
    )
    const groups = groupRequestsByMonth(sortedRequests)
    const sortedMonthKeys = Object.keys(groups).sort((m1, m2) => {
      const date1 = DateTime.fromFormat(m1, "LLLL yyyy", { locale: "es" })
      const date2 = DateTime.fromFormat(m2, "LLLL yyyy", { locale: "es" })
      return date1.toMillis() - date2.toMillis()
    })
    return sortedMonthKeys.map((month) => (
      <div key={month}>
        <div className="requests-list-subheader flex py-2">
          <span
            className="font-bold"
            ref={
              month === DateTime.now().toFormat("MMMM") ?
                requestElementRef
              : null
            }
          >
            {capitalizeFirstLetter(month)}
          </span>
        </div>
        {groups[month].map((vacationRequest, index) => {
          const teamMember = vacationRequest.teamMember
          return (
            <VacationRequestCard
              key={vacationRequest.id || index}
              vacationRequest={vacationRequest}
              teamMember={teamMember}
              onClick={() => {
                setVisibleModal(MY_VACATION_STATES.QUERY)
                setSelectedVacationRequest(vacationRequest)
              }}
              mode="team-member"
              onContextMenu={(e, vacationRequest) => {
                if (
                  !isVacationRequestApproved(vacationRequest) &&
                  !isVacationRequestPending(vacationRequest)
                ) {
                  return
                }
                cmRef?.current.show(e)
                setSelectedVacationRequest(vacationRequest)
              }}
              loading={
                submittingCancel &&
                vacationRequest.id === selectedVacationRequest?.id
              }
            />
          )
        })}
      </div>
    ))
  }

  const hasFilter =
    TEAM_VACATION_REQUESTS_FILTER_OPTIONS.past_requests !==
      selectedFilter.past_requests ||
    TEAM_VACATION_REQUESTS_FILTER_OPTIONS.rejected_requests !==
      selectedFilter.rejected_requests

  const emptyResultsTemplate = ({ hasFilters }) => {
    const emptyMessageTitle =
      hasFilters ? "Sin resultados" : "Nada por aquí (todavía)"
    const emptyMessageContent =
      hasFilters ?
        "No hay solicitudes encontradas con tus filtros, prueba con otros."
      : "No hay solicitudes de vacaciones en el rango seleccionado."
    return (
      <div className="empty-data-team-vacation-requests text-center flex flex-column justify-content-center w-full">
        <i className="pi pi-folder-open"></i>
        <h3 className="title font-cairo">{emptyMessageTitle}</h3>
        <p className="message">{emptyMessageContent}</p>
      </div>
    )
  }

  const renderEmptyState = () => {
    if (summaryLoading) {
      return Array.from({ length: 7 }).map((_, index) => (
        <VacationRequestCardSkeleton key={index} />
      ))
    }
    if (getFilteredRequests(summaryVacationRequests).length === 0) {
      return emptyResultsTemplate({
        hasFilters: hasFilter,
      })
    }
  }

  return (
    <>
      <TeamMemberVacationsMobileHeader
        componentToShow={componentToShow}
        setComponentToShow={setComponentToShow}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        vacationRequests={summaryVacationRequests}
      />
      <Card
        className="calendar-card-container shadow-2"
        style={{ height: screenWidth > 860 ? "90vh" : "72vh", padding: "1rem" }}
      >
        {componentToShow === "Summary" ?
          <>
            <div ref={listContainerRef} className="requests-list my-3">
              {renderVacationRequests()}
              {renderEmptyState()}
              <FloatingButton
                visible={showSeeMore && summaryVacationRequests.length > 0}
                onClick={() => {
                  listContainerRef.current.scrollTo({
                    top: listContainerRef.current.scrollHeight,
                    behavior: "smooth",
                  })
                }}
              />
            </div>
            <TeamMemberVacationRequestsContextMenu
              ref={cmRef}
              vacationRequest={selectedVacationRequest}
              setVisibleModal={setVisibleModal}
              triggerRefresh={triggerRefresh}
              submittingCancel={submittingCancel}
              setSubmittingCancel={setSubmittingCancel}
            />
          </>
        : null}

        {componentToShow === "Calendar" ?
          <>
            <BigCalendar
              key={selectedPeriod}
              loading={calendarLoading}
              events={calendarVacationRequests}
              onView={(e) => {
                opRef?.current?.hide(e)
                setCurrentView(e)
              }}
              views={{
                week: screenWidth > 728,
                month: true,
                placeholder: true,
                // year: screenWidth > 728 ? YearView : false,
              }}
              view={screenWidth <= 728 ? BIG_CALENDAR_VIEWS.MONTH : currentView}
              popup
              style={{ height: "100%" }}
              eventPropGetter={eventPropGetter}
              handleNavigate={handleNavigate}
              formatEventsTemplates={formatVacationEventsTemplates}
              handleEventClick={handleEventClick}
              setGroupedEvents={setGroupedEvents}
              tooltipAccessor={(e) => e.prevTitle}
              date={selectedPeriod[0].toJSDate()}
              min={selectedPeriod[0].startOf("month").toJSDate()}
              max={selectedPeriod[1].endOf("month").toJSDate()}
              groupEvents={false}
            />
            <GroupedEventsOverlayPanel
              opRef={opRef}
              vacationRequests={calendarVacationRequests}
              clickedDay={clickedDay}
              handleEventClick={handleEventClick}
            />
          </>
        : null}
      </Card>
    </>
  )
}

export default MobileTeamMemberData
